import { c as _c } from "react-compiler-runtime";
import { useCallback, useMemo, useRef, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import { gate } from 'hub-http/gates';
import { makeGetColumnWidth, setWidth } from '../../resizing/redux/widthsSlice';
import { useWidthsDispatch, useWidthsSelector } from '../../redux/reduxHooks';
import { ContentComponent } from './getContentComponent';
import { getColumnKey } from '../../table/utils/getColumnKey';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useFDTContextMenuConfig } from '../../contextMenu/context/FDTContextMenuConfigContext';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { shouldBypassContextMenu } from '../../contextMenu/shouldBypassContextMenu';
import { SortTH } from 'foundations-components/transitional/components/Table';
import { DragHandleHoverContainer } from 'foundations-components/transitional/components/DragHandleHoverContainer';
import { DragHandle } from 'foundations-components/transitional/components/DragHandle';
import { useAuth } from '../../auth/context/AuthContext';
import { jsx as _jsx } from "react/jsx-runtime";
const getSortFromDirection = sortDirection => {
  if (sortDirection === -1) {
    return 'ascending';
  }
  if (sortDirection === 1) {
    return 'descending';
  }
  return 'none';
};
const getDirectionFromSort = sort => sort === 'ascending' ? -1 : 1;
const ContextMenuMinWidthContainer = styled.div.withConfig({
  displayName: "ResizableSortTH__ContextMenuMinWidthContainer"
})(["min-width:", "px;"], ({
  $minWidth
}) => $minWidth);
const StopPropogationContainer = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  const handleClick = _temp;
  let t1;
  if ($[0] !== children) {
    t1 = /*#__PURE__*/_jsx("div", {
      onClick: handleClick,
      onContextMenu: handleClick,
      children: children
    });
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const ResizableSortTH = t0 => {
  const $ = _c(78);
  const {
    column,
    sortDirection,
    columnIndex,
    onColumnWidthChange,
    onColumnOrderChange,
    onSortChange,
    children
  } = t0;
  const {
    columnName,
    columnNamespace,
    sortable,
    width: columnWidth,
    minimumColumnWidth,
    reorderable
  } = column;
  let t1;
  if ($[0] !== columnName || $[1] !== columnNamespace) {
    t1 = getColumnKey(columnNamespace, columnName);
    $[0] = columnName;
    $[1] = columnNamespace;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const columnKey = t1;
  const {
    hasGate
  } = useAuth();
  let t2;
  if ($[3] !== hasGate) {
    t2 = hasGate(gate("CRM:Grid:TableHeader"));
    $[3] = hasGate;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasNewTableHeader = t2;
  let t3;
  if ($[5] !== columnKey) {
    t3 = makeGetColumnWidth(columnKey);
    $[5] = columnKey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const getColumnWidth = t3;
  const width = useWidthsSelector(getColumnWidth);
  const widthRef = useRef(width);
  let t4;
  let t5;
  if ($[7] !== width) {
    t4 = () => {
      widthRef.current = width;
    };
    t5 = [width];
    $[7] = width;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== columnName || $[11] !== columnNamespace || $[12] !== onColumnWidthChange) {
    t6 = () => {
      onColumnWidthChange({
        columnNamespace,
        columnName,
        nextWidth: widthRef.current
      });
    };
    $[10] = columnName;
    $[11] = columnNamespace;
    $[12] = onColumnWidthChange;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const onResizeEnd = t6;
  const dispatch = useWidthsDispatch();
  let t7;
  if ($[14] !== columnKey || $[15] !== dispatch || $[16] !== minimumColumnWidth) {
    t7 = nextWidth => dispatch(setWidth({
      columnKey,
      width: Math.max(nextWidth, minimumColumnWidth)
    }));
    $[14] = columnKey;
    $[15] = dispatch;
    $[16] = minimumColumnWidth;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleResize = t7;
  let t8;
  if ($[18] !== columnName || $[19] !== columnNamespace || $[20] !== onSortChange) {
    t8 = t9 => {
      const {
        target: t10
      } = t9;
      const {
        value
      } = t10;
      return onSortChange({
        columnNamespace,
        columnName,
        direction: getDirectionFromSort(value)
      });
    };
    $[18] = columnName;
    $[19] = columnNamespace;
    $[20] = onSortChange;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleSortChange = t8;
  const contextMenuConfig = useFDTContextMenuConfig();
  const triggerContextMenu = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.triggerContextMenu;
  const ContextMenuContentComponent = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.ContextMenuContentComponent;
  let t9;
  if ($[22] !== columnName || $[23] !== columnNamespace || $[24] !== contextMenuConfig) {
    t9 = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.getIsContextMenuOpen({
      row: null,
      columnName,
      columnNamespace
    });
    $[22] = columnName;
    $[23] = columnNamespace;
    $[24] = contextMenuConfig;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const isContextMenuOpen = t9;
  let t10;
  if ($[26] !== triggerContextMenu) {
    t10 = () => {
      if (triggerContextMenu) {
        triggerContextMenu({
          row: null,
          columnName: null,
          columnNamespace: null
        });
      }
    };
    $[26] = triggerContextMenu;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const onCloseContextMenu = t10;
  let t11;
  if ($[28] !== column || $[29] !== columnIndex || $[30] !== columnWidth || $[31] !== handleResize || $[32] !== hasNewTableHeader || $[33] !== onColumnOrderChange || $[34] !== onResizeEnd || $[35] !== sortDirection) {
    t11 = t12 => {
      const {
        arrowsNode,
        children: contentChildren
      } = t12;
      return /*#__PURE__*/_jsx(ContentComponent, {
        arrowsNode: arrowsNode,
        column: column,
        originalWidth: columnWidth,
        columnIndex: columnIndex,
        handleResize: handleResize,
        onResizeEnd: onResizeEnd,
        onColumnOrderChange: onColumnOrderChange,
        hasNewTableHeader: hasNewTableHeader,
        sortDirection: sortDirection,
        children: contentChildren
      });
    };
    $[28] = column;
    $[29] = columnIndex;
    $[30] = columnWidth;
    $[31] = handleResize;
    $[32] = hasNewTableHeader;
    $[33] = onColumnOrderChange;
    $[34] = onResizeEnd;
    $[35] = sortDirection;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const Content = t11;
  let t12;
  if ($[37] !== ContextMenuContentComponent || $[38] !== columnName || $[39] !== columnNamespace || $[40] !== isContextMenuOpen || $[41] !== width) {
    t12 = ContextMenuContentComponent && isContextMenuOpen ? /*#__PURE__*/_jsx(ContextMenuMinWidthContainer, {
      $minWidth: Math.min(width, 500),
      children: /*#__PURE__*/_jsx(StopPropogationContainer, {
        children: /*#__PURE__*/_jsx(ContextMenuContentComponent, {
          columnName: columnName,
          columnNamespace: columnNamespace,
          row: null
        })
      })
    }) : null;
    $[37] = ContextMenuContentComponent;
    $[38] = columnName;
    $[39] = columnNamespace;
    $[40] = isContextMenuOpen;
    $[41] = width;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const contextMenuPopover = t12;
  const externalId = `header-${columnNamespace}-${columnName}`;
  const isContact = columnNamespace === CONTACT_TYPE_ID;
  let t13;
  if ($[43] !== columnName || $[44] !== isContact) {
    t13 = isContact && columnName === "name" ? {
      "data-coaching-tips": "header-name"
    } : {};
    $[43] = columnName;
    $[44] = isContact;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  const coachingTipsSelectors = t13;
  let t14;
  if ($[46] !== columnName || $[47] !== isContact) {
    t14 = isContact ? {
      "data-onboarding": `crm-table-header-cell-${columnName}`
    } : {};
    $[46] = columnName;
    $[47] = isContact;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const onboardingSelectors = t14;
  let t15;
  if ($[49] !== columnName || $[50] !== columnNamespace || $[51] !== triggerContextMenu) {
    t15 = triggerContextMenu ? e => {
      if (shouldBypassContextMenu(e)) {
        return;
      }
      const handled = triggerContextMenu({
        row: null,
        columnNamespace,
        columnName
      });
      if (handled === true) {
        e.preventDefault();
        e.stopPropagation();
      }
    } : undefined;
    $[49] = columnName;
    $[50] = columnNamespace;
    $[51] = triggerContextMenu;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const handleContextMenu = t15;
  const t16 = `${width}px`;
  const t17 = !sortable;
  const t18 = !sortable;
  let t19;
  if ($[53] !== sortDirection) {
    t19 = getSortFromDirection(sortDirection);
    $[53] = sortDirection;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== Content || $[56] !== children || $[57] !== coachingTipsSelectors || $[58] !== columnIndex || $[59] !== externalId || $[60] !== handleContextMenu || $[61] !== handleSortChange || $[62] !== onboardingSelectors || $[63] !== sortDirection || $[64] !== t16 || $[65] !== t17 || $[66] !== t18 || $[67] !== t19 || $[68] !== width) {
    t20 = /*#__PURE__*/_jsx(SortTH, Object.assign({}, coachingTipsSelectors, onboardingSelectors, {
      "data-column-index": columnIndex,
      "data-test-width": width,
      inlineSize: t16,
      padding: "0",
      role: "columnheader",
      "data-table-external-id": externalId,
      "data-test-id": externalId,
      "data-test-sort-direction": sortDirection,
      Content: Content,
      FlexContainer: Fragment,
      disabled: t17,
      "aria-disabled": t18,
      sort: t19,
      onSortChange: handleSortChange,
      onContextMenu: handleContextMenu,
      children: children
    }));
    $[55] = Content;
    $[56] = children;
    $[57] = coachingTipsSelectors;
    $[58] = columnIndex;
    $[59] = externalId;
    $[60] = handleContextMenu;
    $[61] = handleSortChange;
    $[62] = onboardingSelectors;
    $[63] = sortDirection;
    $[64] = t16;
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
    $[68] = width;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  const sortTH = t20;
  let t21;
  if ($[70] !== contextMenuPopover || $[71] !== onCloseContextMenu || $[72] !== sortTH) {
    t21 = contextMenuPopover ? /*#__PURE__*/_jsx(Popover, {
      arrowSize: "none",
      closeOnOutsideClick: true,
      content: contextMenuPopover,
      "data-test-id": "context-menu-popover",
      onOpenChange: onCloseContextMenu,
      open: true,
      placement: "bottom right",
      children: sortTH
    }) : sortTH;
    $[70] = contextMenuPopover;
    $[71] = onCloseContextMenu;
    $[72] = sortTH;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== hasNewTableHeader || $[75] !== reorderable || $[76] !== t21) {
    t22 = /*#__PURE__*/_jsx(ResizableSortTHWrapper, {
      reorderable: reorderable,
      hasNewTableHeader: hasNewTableHeader,
      children: t21
    });
    $[74] = hasNewTableHeader;
    $[75] = reorderable;
    $[76] = t21;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  return t22;
};
function ResizableSortTHWrapper(t0) {
  const $ = _c(3);
  const {
    children,
    reorderable,
    hasNewTableHeader
  } = t0;
  if (hasNewTableHeader) {
    return children;
  }
  let t1;
  if ($[0] !== children || $[1] !== reorderable) {
    t1 = reorderable ? /*#__PURE__*/_jsx(DragHandleHoverContainer, {
      children: /*#__PURE__*/_jsx(DragHandle, {
        children: children
      })
    }) : children;
    $[0] = children;
    $[1] = reorderable;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export default ResizableSortTH;
function _temp(e) {
  e.stopPropagation();
}